import React, { useEffect, useRef } from 'react';
import './ContextMenu.css';

export interface LinkMenuInfo {
  id: string;
  type: string;
  sourceId: string;
  targetId: string;
}

interface LinkContextMenuProps {
  position: { x: number; y: number };
  link: LinkMenuInfo;
  /** Resolve a node ID to its display name. */
  nodeName: (id: string) => string;
  /** IDs of ghost nodes (invisible but still in graph). */
  ghostNodeIds: Set<string>;
  /** Called to hide one side of the link (transitive). */
  onHideSide: (linkId: string, nodeIdToHide: string, nodeIdToKeep: string) => void;
  /** Called to show a previously hidden side of the link. */
  onShowSide: (nodeId: string) => void;
  onClose: () => void;
}

const LinkContextMenu: React.FC<LinkContextMenuProps> = ({
  position,
  link,
  nodeName,
  ghostNodeIds,
  onHideSide,
  onShowSide,
  onClose,
}) => {
  const menuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        onClose();
      }
    };
    const handleEscapeKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };
    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleEscapeKey);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscapeKey);
    };
  }, [onClose]);

  useEffect(() => {
    if (menuRef.current) {
      const firstButton = menuRef.current.querySelector('button');
      if (firstButton) firstButton.focus();
    }
  }, []);

  const sourceHidden = ghostNodeIds.has(link.sourceId);
  const targetHidden = ghostNodeIds.has(link.targetId);

  const sourceName = nodeName(link.sourceId);
  const targetName = nodeName(link.targetId);

  return (
    <div
      ref={menuRef}
      className="context-menu"
      role="menu"
      aria-label="Link context menu"
      tabIndex={0}
      style={{
        position: 'fixed',
        left: `${position.x}px`,
        top: `${position.y}px`,
        background: '#fff',
        border: '1px solid #ccc',
        boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
        zIndex: 10,
        listStyle: 'none',
        padding: 0,
        margin: 0,
      }}
      aria-modal="true"
    >
      <div className="context-menu-title">{link.type}</div>
      <ul>
        {sourceHidden ? (
          <li style={{ padding: 0, margin: 0, border: 'none', background: 'none' }}>
            <button
              className="context-menu-item"
              type="button"
              role="menuitem"
              tabIndex={0}
              onClick={() => { onShowSide(link.sourceId); onClose(); }}
            >
              Show {sourceName}
            </button>
          </li>
        ) : !targetHidden ? (
          <li style={{ padding: 0, margin: 0, border: 'none', background: 'none' }}>
            <button
              className="context-menu-item context-menu-item--danger"
              type="button"
              role="menuitem"
              tabIndex={0}
              onClick={() => { onHideSide(link.id, link.sourceId, link.targetId); onClose(); }}
            >
              Hide {sourceName}
            </button>
          </li>
        ) : null}

        {targetHidden ? (
          <li style={{ padding: 0, margin: 0, border: 'none', background: 'none' }}>
            <button
              className="context-menu-item"
              type="button"
              role="menuitem"
              tabIndex={0}
              onClick={() => { onShowSide(link.targetId); onClose(); }}
            >
              Show {targetName}
            </button>
          </li>
        ) : !sourceHidden ? (
          <li style={{ padding: 0, margin: 0, border: 'none', background: 'none' }}>
            <button
              className="context-menu-item context-menu-item--danger"
              type="button"
              role="menuitem"
              tabIndex={0}
              onClick={() => { onHideSide(link.id, link.targetId, link.sourceId); onClose(); }}
            >
              Hide {targetName}
            </button>
          </li>
        ) : null}
      </ul>
    </div>
  );
};

export default LinkContextMenu;
